import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { AUTH_CONFIG, ERROR_MESSAGES } from '../config/constants';
import { logAuthState, validateUserData, validateToken } from '../utils/authUtils';

const ProtectedRoute = ({ children, allowedUserTypes = [] }) => {
  const { isAuthenticated, user, loading, validateAuthState, authState } = useContext(AuthContext);
  const location = useLocation();
  const [authValidated, setAuthValidated] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [gracePeriod, setGracePeriod] = useState(true);
  
  // Enhanced debug logging to help diagnose issues
  useEffect(() => {
    if (AUTH_CONFIG.DEBUG.ENABLED && AUTH_CONFIG.DEBUG.LOG_NAVIGATION) {

      logAuthState('PROTECTED_ROUTE_RENDER', { 
        path: location.pathname,
        isAuthenticated,
        userType: user?.userType,
        profileCompleted: user?.profileCompleted,
        allowedTypes: allowedUserTypes,
        authInitialized: authState?.initialized,
        loading,
        gracePeriod,
        authValidated
      });
    }
  }, [location.pathname, user, allowedUserTypes, isAuthenticated, authState, loading, gracePeriod, authValidated]);
  
  // Validate authentication state with a grace period
  useEffect(() => {
    // Skip validation during loading or if already validated
    if (loading || authValidated) return;
    
    // Determine grace period based on route - reduced timeouts for better UX
    const isRecruiterRoute = location.pathname.startsWith('/recruiter/');
    const gracePeriodTimeout = isRecruiterRoute ? 1000 : 500;
    
    if (isRecruiterRoute) {
      // Reserved for recruiter-specific grace period logic if needed
    }
    
    // Allow a brief grace period for auth state to settle
    const timer = setTimeout(() => {
      setGracePeriod(false);
      
      // Only validate if we're supposed to be authenticated
      if (isAuthenticated) {
        try {
          // Validate the authentication state - treat missing validateAuthState as valid
          const isValid = validateAuthState ? validateAuthState() : true;
          
          if (!isValid) {
            logAuthState('PROTECTED_ROUTE_AUTH_INVALID', { 
              path: location.pathname,
              reason: 'validation_failed'
            });
            // Try to refresh auth state before failing
            setTimeout(() => {
              const retryValid = validateAuthState ? validateAuthState() : true;
              if (!retryValid) {
                // Check if user has minimal required data to proceed anyway
                if (user && user.userType && user.id && user.email) {
                  console.warn('[WARNING] Auth validation bypassed - minimal user data present');
                  setAuthValidated(true);
                  return;
                }
                setAuthError(ERROR_MESSAGES.AUTH.DATA_INTEGRITY);
              } else {
                setAuthValidated(true);
              }
            }, 500);
            return;
          }
          
          setAuthValidated(true);
        } catch (err) {
          logAuthState('PROTECTED_ROUTE_VALIDATION_ERROR', { error: err.message });
          // Don't fail validation for validation errors - just log them
          setAuthValidated(true);
        }
      } else {
        // If not authenticated, still clear grace period
        setAuthValidated(true);
      }
    }, gracePeriodTimeout);
    
    // Force clear grace period after maximum timeout
    const maxTimer = setTimeout(() => {

      setGracePeriod(false);
      setAuthValidated(true);
    }, 5000); // Increased to 5 seconds to give more time for auth to settle
    
    return () => {
      clearTimeout(timer);
      clearTimeout(maxTimer);
    };
  }, [loading, isAuthenticated, validateAuthState, authValidated, location.pathname]);
  
  // Handle loading state with a more informative UI
  if (loading || (isAuthenticated && gracePeriod)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="animate-pulse text-center">
          <p className="text-lg text-gray-600">Verifying your access...</p>
        </div>
      </div>
    );
  }
  
  // Handle authentication validation errors with recovery attempt
  if (authError && !gracePeriod) {
    // Before redirecting, attempt to recover from localStorage
    const storedToken = localStorage.getItem(AUTH_CONFIG.TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_CONFIG.USER_KEY);

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const tokenValidation = validateToken(storedToken);

        if (tokenValidation.valid && parsedUser.id && parsedUser.userType) {
          // Token and user data exist - let AuthContext handle recovery
          // Clear error and allow one more validation cycle
          setAuthError(null);
          setAuthValidated(false);
          return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
              <div className="animate-pulse text-center">
                <p className="text-lg text-gray-600">Recovering session...</p>
              </div>
            </div>
          );
        }
      } catch (err) {
        console.error('Failed to recover auth from localStorage:', err);
      }
    }

    logAuthState('PROTECTED_ROUTE_AUTH_ERROR_REDIRECT', {
      error: authError,
      redirectTo: '/auth/candidate'
    });
    // Redirect to auth page with error message
    const userType = user?.userType || 'candidate';
    return <Navigate to={`/auth/${userType}?error=${encodeURIComponent(authError)}`} replace />;
  }

  // If not authenticated, redirect to appropriate auth page
  if (!isAuthenticated) {
    // Determine which auth page to redirect to based on the requested route
    let userType = 'candidate'; // Default to candidate
    
    // Check if the route is specifically for affiliates
    if (location.pathname.startsWith('/affiliate/')) {
      logAuthState('PROTECTED_ROUTE_UNAUTHENTICATED_AFFILIATE', { 
        path: location.pathname
      });
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Affiliate Access Required</h2>
            <p className="text-gray-700 mb-4">Please use the access link from your approval email.</p>
            <p className="text-gray-600 mb-4">If you haven't applied yet, you can join our affiliate program below.</p>
            <a
              href="/affiliate/signup"
              className="inline-block bg-purple-600 text-white px-6 py-2 rounded hover:bg-purple-700 transition-colors"
            >
              Apply to Affiliate Program
            </a>
          </div>
        </div>
      );
    }
    
    // Check if the route is specifically for recruiters
    if (location.pathname.startsWith('/recruiter/')) {
      // For recruiter routes, show a special message instead of redirecting to auth pages
      logAuthState('PROTECTED_ROUTE_UNAUTHENTICATED_RECRUITER', { 
        path: location.pathname
      });
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Recruiter Access Required</h2>
            <p className="text-gray-700 mb-4">Please use your invitation link to access the recruiter dashboard.</p>
            <p className="text-gray-600 mb-4">If you don't have an invitation link, please contact your employer.</p>
            <button 
              onClick={() => window.location.href = 'mailto:support@example.com?subject=Request%20Recruiter%20Access'}
              className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition-colors"
            >
              Request Access
            </button>
          </div>
        </div>
      );
    }
    
    // Check if the route is specifically for employers based on allowedUserTypes
    // This is more reliable than checking pathname since employer routes don't always have /employer/ prefix
    if (allowedUserTypes.includes('employer') && !allowedUserTypes.includes('candidate')) {
      userType = 'employer';
    } else if (location.pathname.startsWith('/employer/') || 
               location.pathname === '/dashboard' || 
               location.pathname === '/complete-profile' ||
               location.pathname === '/inbox' ||
               location.pathname === '/applications' ||
               location.pathname === '/database' ||
               location.pathname === '/posted-jobs' ||
               location.pathname === '/post-job' ||
               location.pathname === '/analytics' ||
               location.pathname === '/branding' ||
               location.pathname === '/billing' ||
               location.pathname === '/recruiters' ||
               location.pathname === '/recruiter-performance') {
      userType = 'employer';
    }
    
    logAuthState('PROTECTED_ROUTE_UNAUTHENTICATED', { 
      path: location.pathname,
      redirectTo: `/auth/${userType}`,
      inferredUserType: userType,
      allowedUserTypes: allowedUserTypes
    });
    return <Navigate to={`/auth/${userType}`} state={{ from: location }} replace />;
  }
  
  // Validate user data integrity
  const validatedUser = validateUserData(user);
  if (!validatedUser) {
    // Determine correct auth page based on route context
    let authType = 'candidate';
    if (allowedUserTypes.includes('employer') && !allowedUserTypes.includes('candidate')) {
      authType = 'employer';
    } else if (location.pathname.startsWith('/employer/') || 
               location.pathname.startsWith('/dashboard') ||
               location.pathname === '/inbox' ||
               location.pathname === '/applications' ||
               location.pathname === '/database' ||
               location.pathname === '/posted-jobs' ||
               location.pathname === '/post-job' ||
               location.pathname === '/analytics' ||
               location.pathname === '/branding' ||
               location.pathname === '/billing' ||
               location.pathname === '/support' ||
               location.pathname === '/notifications' ||
               location.pathname === '/profile' ||
               location.pathname === '/recruiters' ||
               location.pathname === '/recruiter-performance') {
      authType = 'employer';
    }
    
    logAuthState('PROTECTED_ROUTE_INVALID_USER_DATA', { 
      path: location.pathname,
      redirectTo: `/auth/${authType}`,
      allowedUserTypes: allowedUserTypes
    });
    return <Navigate to={`/auth/${authType}`} state={{ error: ERROR_MESSAGES.AUTH.DATA_INTEGRITY }} replace />;
  }

  // If user types are specified, check if the user has the required type
  if (allowedUserTypes.length > 0 && validatedUser) {
    // Use validatedUser.userType as the primary source of truth
    const userType = validatedUser.userType;
    
    // Special case for admin routes - check for admin token
    if (allowedUserTypes.includes('admin') && location.pathname.startsWith('/admin/')) {
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        return children;
      } else {
        return <Navigate to="/admin/login" replace />;
      }
    }
    
    if (!userType) {
      // Critical error - user type is missing
      logAuthState('PROTECTED_ROUTE_MISSING_USER_TYPE', { path: location.pathname });
      return <Navigate to="/auth/candidate" state={{ error: ERROR_MESSAGES.AUTH.USER_TYPE_MISSING }} replace />;
    }
    
    if (!allowedUserTypes.includes(userType)) {
      // Additional validation for specific user types
      if (userType === 'employer' && !validatedUser.company && !validatedUser.companyName) {
        console.error('Employer user missing company information:', validatedUser);
        logAuthState('PROTECTED_ROUTE_INVALID_EMPLOYER', { 
          path: location.pathname,
          reason: 'missing_company_info'
        });
      }
      
      if (userType === 'recruiter' && !validatedUser.employerId) {
        console.warn('[WARNING] Recruiter user missing employerId in token (will be loaded from backend):', validatedUser);
        logAuthState('PROTECTED_ROUTE_INVALID_RECRUITER', { 
          path: location.pathname,
          reason: 'missing_employer_id'
        });
        // Note: This is not blocking - employerId will be fetched from recruiters.json when needed
      }
      
      // User type mismatch - log detailed information
      let redirectPath;
      
      // Handle recruiter user type specifically
      if (userType === 'recruiter') {
        redirectPath = '/recruiter/dashboard';
      } else {
        // For other user types
        redirectPath = userType === 'candidate' ? '/jobs' : '/employer/dashboard';
      }
      
      logAuthState('PROTECTED_ROUTE_USER_TYPE_MISMATCH', { 
        path: location.pathname,
        userType,
        allowedTypes: allowedUserTypes,
        redirectTo: redirectPath,
        userDetails: {
          id: validatedUser.id,
          email: validatedUser.email,
          hasCompany: !!(validatedUser.company || validatedUser.companyName),
          hasEmployerId: !!validatedUser.employerId
        }
      });
      
      // Redirect to the appropriate dashboard based on user type
      return <Navigate to={redirectPath} replace />;
    }
  }
  
  // Handle profile completion redirects based on user type
  if (validatedUser && validatedUser.profileCompleted === false) {
    // Check if we're already on a profile completion page
    const isOnCompletionPage = 
      location.pathname === '/candidate/complete-profile' || 
      location.pathname === '/complete-profile' || 
      location.pathname === '/employer/complete-profile';
    
    // Only redirect if not already on a completion page
    if (!isOnCompletionPage) {
      // Use validatedUser.userType as the source of truth
      const userType = validatedUser.userType;
      
      if (!userType) {
        // Critical error - user type is missing
        logAuthState('PROTECTED_ROUTE_MISSING_USER_TYPE_COMPLETION', { path: location.pathname });
        return <Navigate to="/auth/candidate" state={{ error: ERROR_MESSAGES.AUTH.USER_TYPE_MISSING }} replace />;
      }
      
      // Log the redirect
      logAuthState('PROTECTED_ROUTE_PROFILE_COMPLETION_REDIRECT', { 
        path: location.pathname,
        userType,
        redirectTo: userType === 'candidate' ? '/candidate/complete-profile' : '/employer/complete-profile'
      });
      // Determine the correct completion page based on user type
      const completionPath = userType === 'candidate' 
        ? '/candidate/complete-profile' 
        : '/employer/complete-profile';
      
      return <Navigate to={completionPath} replace />;
    }
  }
  
  // If the user's profile is complete and they're trying to access a completion page,
  // redirect to the appropriate dashboard
  if (validatedUser && validatedUser.profileCompleted === true) {
    const isOnCompletionPage = 
      location.pathname === '/candidate/complete-profile' || 
      location.pathname === '/complete-profile' || 
      location.pathname === '/employer/complete-profile';
    
    if (isOnCompletionPage) {
      // Use validatedUser.userType as the source of truth
      const userType = validatedUser.userType;
      
      if (!userType) {
        // Critical error - user type is missing
        logAuthState('PROTECTED_ROUTE_MISSING_USER_TYPE_DASHBOARD', { path: location.pathname });
        return <Navigate to="/auth/candidate" state={{ error: ERROR_MESSAGES.AUTH.USER_TYPE_MISSING }} replace />;
      }
      
      // Log the redirect
      logAuthState('PROTECTED_ROUTE_DASHBOARD_REDIRECT', { 
        path: location.pathname,
        userType,
        redirectTo: userType === 'candidate' ? '/jobs' : userType === 'recruiter' ? '/recruiter/dashboard' : '/employer/dashboard'
      });
      const dashboardPath = userType === 'candidate' 
        ? '/jobs' 
        : userType === 'recruiter'
          ? '/recruiter/dashboard'
          : '/employer/dashboard';
      
      return <Navigate to={dashboardPath} replace />;
    }
  }
  
  // Log successful access
  logAuthState('PROTECTED_ROUTE_ACCESS_GRANTED', { 
    path: location.pathname,
    userType: validatedUser?.userType,
    profileCompleted: validatedUser?.profileCompleted
  });
  // Render children with error boundary
  try {
    return children;
  } catch (error) {
    // Log the error
    logAuthState('PROTECTED_ROUTE_RENDER_ERROR', { 
      path: location.pathname,
      error: error.message
    });
    // Fallback UI
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
          <p className="text-gray-700 mb-4">We encountered an error while loading this page.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
};

export default ProtectedRoute;
